// CartPage.js — localStorage exclusivo, key 'cart', formato unificado con ProductDetailPage
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import '../App.css';

// ─── helpers ────────────────────────────────────────────────────────────────
const CART_KEY = 'cart';

/**
 * Lee el carrito desde localStorage y devuelve el formato normalizado
 * que usa el resto de la UI.
 *
 * Formato en storage (lo que escribe ProductDetailPage):
 *   [{ producto_id, cantidad, producto: { nombre, precio, imagen_url } }]
 *
 * Formato normalizado que consume esta página:
 *   { items: [...], cantidad_items: number, subtotal: number }
 */
function readCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const items = raw ? JSON.parse(raw) : [];

    return {
      items: items.map(item => ({
        producto_id: item.producto_id,
        producto: {
          id: item.producto_id,
          nombre: item.producto?.nombre || 'Producto',
          imagen_url: item.producto?.imagen_url || null,
          precio_actual: item.producto?.precio || 0,
          stock: 100                          // sin dato real desde localStorage; se valida en checkout
        },
        cantidad: item.cantidad,
        precio_unitario: item.producto?.precio || 0,
        subtotal: (item.producto?.precio || 0) * item.cantidad
      })),
      cantidad_items: items.reduce((s, i) => s + i.cantidad, 0),
      subtotal: items.reduce((s, i) => s + (i.producto?.precio || 0) * i.cantidad, 0)
    };
  } catch {
    return { items: [], cantidad_items: 0, subtotal: 0 };
  }
}

/**
 * Escribe el array crudo de items en localStorage y dispara el evento
 * para que Header y otros componentes se actualicen.
 */
function writeCart(rawItems) {
  localStorage.setItem(CART_KEY, JSON.stringify(rawItems));
  window.dispatchEvent(new Event('cart-updated'));
}

/** Lee el array crudo (sin mapear) del localStorage. */
function readRawCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const CartPage = () => {
  const [cart, setCart] = useState(readCart);
  const navigate = useNavigate();

  // ── escuchar actualizaciones del carrito desde otros componentes ────────
  const refresh = useCallback(() => {
    const updatedCart = readCart();
    // Corregir items de afiliación que no tengan imagen_url o tengan URL de placeholder
    const rawCart = readRawCart();
    let needsUpdate = false;
    const correctedCart = rawCart.map(item => {
      const isAfiliacion = item.producto_id === 'afiliacion-achipo' || 
                          String(item.producto_id || '').includes('afiliacion') ||
                          String(item.producto?.nombre || '').toLowerCase().includes('afiliación') ||
                          String(item.producto?.nombre || '').toLowerCase().includes('afiliacion');
      
      if (isAfiliacion) {
        const imagenUrl = item.producto?.imagen_url || '';
        const tienePlaceholder = imagenUrl.includes('via.placeholder') || 
                                imagenUrl.includes('placeholder.com') ||
                                !imagenUrl ||
                                imagenUrl === '';
        
        if (tienePlaceholder) {
          needsUpdate = true;
          return {
            ...item,
            producto: {
              ...item.producto,
              imagen_url: `${process.env.PUBLIC_URL || ''}/achipo.jpg`
            }
          };
        }
      }
      return item;
    });
    
    if (needsUpdate) {
      writeCart(correctedCart);
      return readCart();
    }
    
    return updatedCart;
  }, []);

  // Actualizar estado cuando cambia el carrito (incluye evento + llamadas locales)
  const handleCartUpdate = useCallback(() => {
    setCart(refresh());
  }, [refresh]);

  useEffect(() => {
    window.addEventListener('cart-updated', handleCartUpdate);
    // 'storage' se dispara solo desde otras pestañas; lo escuchamos también
    window.addEventListener('storage', handleCartUpdate);
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, [handleCartUpdate]);

  // ── operaciones sobre el carrito (solo localStorage) ───────────────────
  const handleRemoveItem = (producto_id) => {
    const updated = readRawCart().filter(item => item.producto_id !== producto_id);
    writeCart(updated);
    setCart(readCart());
  };

  const handleUpdateQuantity = (producto_id, newQty) => {
    if (newQty < 1) return handleRemoveItem(producto_id);

    const updated = readRawCart().map(item =>
      item.producto_id === producto_id ? { ...item, cantidad: newQty } : item
    );
    writeCart(updated);
    // Actualizar estado local de inmediato (evita esperar al evento)
    setCart(readCart());
  };

  const handleClearCart = () => {
    if (!window.confirm('¿Estás seguro de que deseas vaciar el carrito?')) return;
    writeCart([]);
    setCart(readCart());
  };

  // ── checkout ────────────────────────────────────────────────────────────
  const proceedToCheckout = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      if (window.confirm('Debes iniciar sesión para continuar con la compra. ¿Deseas ir al login?')) {
        navigate('/login', { state: { from: '/cart' } });
      }
      return;
    }
    navigate('/checkout');
  };

  // ── cálculo de envío ────────────────────────────────────────────────────
  // Verificar si el carrito contiene solo afiliaciones (no requieren envío físico)
  const tieneSoloAfiliaciones = cart.items.every(
    item => item.producto_id === 'afiliacion-achipo'
  );
  
  // Verificar si hay productos de prueba (envío gratuito)
  // Detectar por nombre, ID, o cualquier campo que contenga "prueba" o "test"
  const tieneProductosPrueba = cart.items.some(item => {
    const productoId = String(item.producto_id || '').toLowerCase();
    const nombreProducto = String(item.producto?.nombre || '').toLowerCase();
    const descripcion = String(item.producto?.descripcion || '').toLowerCase();
    const categoria = String(item.producto?.categoria || '').toLowerCase();
    
    // Buscar en múltiples campos
    return nombreProducto.includes('prueba') || 
           nombreProducto.includes('test') ||
           productoId.includes('prueba') ||
           productoId.includes('test') ||
           descripcion.includes('prueba') ||
           descripcion.includes('test') ||
           categoria.includes('prueba') ||
           categoria.includes('test');
  });
  
  // Si solo hay afiliaciones o hay productos de prueba, el envío es 0
  const shippingCost = (tieneSoloAfiliaciones || tieneProductosPrueba) ? 0 : (cart.items.length > 0 ? 5000 : 0);
  const finalTotal = cart.subtotal + shippingCost;

  const defaultImg = `${process.env.PUBLIC_URL || ''}/contenido-no-disponible.jpeg`;

  // ── render: carrito vacío ───────────────────────────────────────────────
  if (cart.items.length === 0) {
    return (
      <div className="main-container">
        <Header />
        <div className="cart-container cart-container--empty">
          <div className="cart-empty">
            <h1 className="cart-empty-title">Carrito vacío</h1>
            <p className="cart-empty-text">Aún no tienes productos. Cuando agregues algo, aparecerá aquí.</p>
            <Link to="/shop" className="cart-empty-link">Ver tienda</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── render: carrito con items ───────────────────────────────────────────
  return (
    <div className="main-container">
      <Header />

      <div className="cart-container">
        <header className="cart-header-minimal">
          <Link to="/shop" className="cart-back">Tienda</Link>
          <h1 className="cart-title-minimal">
            Carrito <span className="cart-title-count">({cart.cantidad_items} {cart.cantidad_items === 1 ? 'producto' : 'productos'})</span>
          </h1>
        </header>

        <div className="cart-layout">
          <section className="cart-list">
            <div className="cart-list-toolbar">
              <button type="button" onClick={handleClearCart} className="cart-clear" title="Vaciar carrito">
                Vaciar carrito
              </button>
            </div>

            <ul className="cart-list-items">
              {cart.items.map(item => (
                <li key={item.producto_id} className="cart-row">
                  <div className="cart-row-image">
                    <img
                      src={item.producto.imagen_url || defaultImg}
                      alt={item.producto.nombre}
                      onError={(e) => {
                        if (!e.target.src.includes('contenido-no-disponible.jpeg')) e.target.src = defaultImg;
                      }}
                    />
                  </div>
                  <div className="cart-row-info">
                    <h3 className="cart-row-name">{item.producto.nombre}</h3>
                    <p className="cart-row-unit">${item.precio_unitario.toLocaleString('es-CL')} c/u</p>
                  </div>
                  <div className="cart-row-qty">
                    <button
                      type="button"
                      onClick={() => handleUpdateQuantity(item.producto_id, item.cantidad - 1)}
                      className="cart-qty-btn"
                      aria-label="Menos"
                    >
                      −
                    </button>
                    <span className="cart-qty-value">{item.cantidad}</span>
                    <button
                      type="button"
                      onClick={() => handleUpdateQuantity(item.producto_id, item.cantidad + 1)}
                      className="cart-qty-btn"
                      aria-label="Más"
                    >
                      +
                    </button>
                  </div>
                  <div className="cart-row-total">${item.subtotal.toLocaleString('es-CL')}</div>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.producto_id)}
                    className="cart-row-remove"
                    title="Quitar"
                    aria-label="Quitar producto"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>

            <div className="cart-more">
              <Link to="/shop" className="cart-more-link">Seguir comprando</Link>
            </div>
          </section>

          <aside className="cart-summary">
            <div className="cart-summary-inner">
              <table className="cart-summary-table">
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td>${cart.subtotal.toLocaleString('es-CL')}</td>
                  </tr>
                  <tr>
                    <td>Envío</td>
                    <td>${shippingCost.toLocaleString('es-CL')}</td>
                  </tr>
                  <tr className="cart-summary-total">
                    <td>Total</td>
                    <td>${finalTotal.toLocaleString('es-CL')}</td>
                  </tr>
                </tbody>
              </table>

              <button type="button" onClick={proceedToCheckout} className="cart-checkout-btn">
                Ir a pagar
              </button>

              {!localStorage.getItem('access_token') && (
                <p className="cart-login-hint">Inicia sesión para completar la compra</p>
              )}

              <p className="cart-secure">Pago seguro</p>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;